<template>
  <div class="timeclick-page">
    <!-- Left Panel: Clock & Branding -->
    <div class="timeclick-left">
      <div class="left-content">
        <div class="clock-date">
          {{ currentDateThai }}
        </div>
        <div class="clock-time">
          <span class="clock-hours">{{ hours }}:{{ minutes }}</span>
          <span class="clock-period">{{ period }}</span>
        </div>
        <div v-if="clockInTimeDisplayed" class="clock-in-info">
          เริ่มเข้างาน เวลา {{ clockInTimeDisplayed }}
        </div>
      </div>
      <div class="left-branding">
        <div class="branding-org">MUIC</div>
        <div class="branding-location">
          <a-icon type="environment" class="mr-5" />IC Portal Time Clock
        </div>
        <img src="/images/muiclogoOf.png" alt="MUIC Logo" class="branding-logo" />
        <!--  
          <button class="doorbell-btn" :class="{ 'doorbell-ringing': isRinging, 'doorbell-locked': !audioUnlocked }" @click="ringDoorbell" :disabled="isRinging">
            <span class="doorbell-icon">{{ audioUnlocked ? '🔔' : '🔇' }}</span>
            <span class="doorbell-label">
              {{ isRinging ? 'Ringing...' : (audioUnlocked ? 'Ring Bell' : 'Click to Enable Sound') }}
            </span>
          </button>
        -->
      </div>
    </div>

    <!-- Right Panel: User Actions -->
    <div class="timeclick-right">
      <div class="right-content">
        <!-- Greeting -->
        <div class="greeting-section">
          <h1 class="greeting-text">{{ greetingText }}, {{ displayName }}</h1>
          <p class="greeting-subtitle">Let's get to work.</p>
          <div v-if="clockInMoment" class="work-timer">
            เริ่มงานไปแล้ว {{ workDuration }}
          </div>
        </div>

        <!-- Avatar -->
        <div class="avatar-section">
          <div class="avatar-line"></div>
          <div class="avatar-wrapper">
            <a-avatar :size="120" :src="avatarUrl" icon="user" class="user-avatar" />
          </div>
          <div class="avatar-line"></div>
        </div>

        <!-- Action Buttons -->
        <div class="actions-section">
          <a-button
            class="btn-clock-in"
            size="large"
            :loading="clockInLoading"
            @click="handleClockIn"
          >
            <a-icon v-if="!clockInLoading" type="login" />
            Clock in
          </a-button>
          <a-button
            class="btn-request"
            size="large"
            :loading="clockOutLoading"
            @click="handleClockOut"
          >
            <a-icon type="logout" />
            Clock out
          </a-button>
        </div>

        <!-- Cancel / Back -->
        <div class="cancel-section">
          <a href="javascript:;" class="cancel-link" @click="handleCancel">
            <a-icon type="arrow-left" class="mr-5" />Cancel
          </a>
        </div>
      </div>

      <!-- Success / Error State Overlay -->
      <transition name="fade">
        <div v-if="resultState" class="result-overlay">
          <div class="result-card">
            <a-icon
              :type="resultState === 'success' ? 'check-circle' : 'close-circle'"
              :class="resultState === 'success' ? 'result-icon-success' : 'result-icon-error'"
              class="result-icon"
            />
            <h2 class="result-title">{{ resultTitle }}</h2>
            <p class="result-message">{{ resultMessage }}</p>
            <p v-if="resultState === 'success'" class="result-time">{{ clockedTime }}</p>
            <a-button class="btn-done" @click="resetState">
              Done
            </a-button>
          </div>
        </div>
      </transition>

      <!-- FCM Notification Toast -->
      <transition name="slide-notification">
        <div v-if="showNotification" class="fcm-notification-toast" @click="dismissNotification">
          <div class="notif-icon-wrapper">
            <span class="notif-bell">🔔</span>
          </div>
          <div class="notif-body">
            <p class="notif-title">{{ notificationData.title }}</p>
            <p class="notif-message">{{ notificationData.body }}</p>
          </div>
          <button class="notif-close" @click.stop="dismissNotification">
            <a-icon type="close" />
          </button>
        </div>
      </transition>

      <!-- Notification Badge -->
      <div v-if="notifications.length > 0" class="notif-badge" @click="showNotifPanel = !showNotifPanel">
        <a-icon type="bell" />
        <span class="notif-count">{{ notifications.length }}</span>
      </div>

      <!-- Notification History Panel -->
      <transition name="slide-panel">
        <div v-if="showNotifPanel" class="notif-panel">
          <div class="notif-panel-header">
            <h4>Notifications</h4>
            <a-button size="small" type="link" @click="clearAllNotifications">Clear All</a-button>
          </div>
          <div class="notif-panel-list">
            <div v-for="(n, idx) in notifications" :key="idx" class="notif-panel-item">
              <div class="notif-panel-dot"></div>
              <div class="notif-panel-content">
                <p class="notif-panel-title">{{ n.title }}</p>
                <p class="notif-panel-body">{{ n.body }}</p>
                <small class="notif-panel-time">{{ n.time }}</small>
              </div>
            </div>
            <div v-if="notifications.length === 0" class="notif-panel-empty">
              No notifications
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import "moment/locale/th";
import { messaging, getToken, onMessage } from "@/services/firebase";

export default {
  name: "TimeClick",
  data() {
    return {
      hours: "00",
      minutes: "00",
      seconds: "00",
      period: "am",
      currentDateThai: "",
      clockInterval: null,
      avatarUrl: "",
      clockInLoading: false,
      resultState: null, // null | 'success' | 'error'
      resultTitle: "",
      resultMessage: "",
      clockedTime: "",
      isRinging: false,
      audioCtx: null,
      notifAudioCtx: null,
      // FCM
      fcmToken: null,
      showNotification: false,
      notificationData: { title: '', body: '' },
      notificationTimeout: null,
      notifications: [],
      showNotifPanel: false,
      // Work Timer
      clockInTimeDisplayed: "",
      clockInMoment: null,
      workDuration: "00:00:00",
      clockOutLoading: false,
    };
  },
  computed: {
    displayName() {
      const rules = this.$store.state.role.RulesUser;
      if (rules && rules.username) {
        return rules.username;
      }
      if (this.$keycloak && this.$keycloak.authenticated) {
        return this.$keycloak.fullName || "";
      }
      return "";
    },
    greetingText() {
      const h = parseInt(this.hours);
      const isPM = this.period === "pm";
      const hour24 = isPM && h !== 12 ? h + 12 : (!isPM && h === 12 ? 0 : h);

      if (hour24 >= 5 && hour24 < 12) return "Good morning";
      if (hour24 >= 12 && hour24 < 17) return "Good afternoon";
      return "Good evening";
    },
    userRole() {
      const rules = this.$store.state.role.RulesUser;
      if (rules && rules.roles) {
        if (rules.roles.includes("staff")) return "Staff";
        if (rules.roles.includes("faculty")) return "Faculty";
      }
      return "";
    },
  },
  mounted() {
    this.updateClock();
    this.clockInterval = setInterval(this.updateClock, 1000);
    this.loadAvatar();
    this.initFCM();
    // this.checkCurrentStatus(); // Disabled API call as requested

    // Pre-init AudioContext. If the user navigated here within the SPA, this may instantly unlock.
    const ctx = this.ensureAudioContext();
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    // Fallback: unlock on ANY interaction in case of a hard browser refresh
    const unlockAudio = async () => {
      if (this.notifAudioCtx && this.notifAudioCtx.state === 'suspended') {
        await this.notifAudioCtx.resume().catch(() => {});
      }
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
    };
    document.addEventListener('click', unlockAudio, { once: true });
    document.addEventListener('touchstart', unlockAudio, { once: true });

    // Listen for messages from Service Worker (background notifications)
    this.swMessageHandler = (event) => {
      if (event.data && event.data.type === 'FCM_BACKGROUND_MESSAGE') {
        // eslint-disable-next-line no-console
        console.log('Background FCM via SW:', event.data);
        this.handleIncomingNotification({
          notification: {
            title: event.data.title,
            body: event.data.body,
          },
        });
      }
    };
    navigator.serviceWorker.addEventListener('message', this.swMessageHandler);
  },
  beforeDestroy() {
    if (this.clockInterval) {
      clearInterval(this.clockInterval);
    }
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout);
    }
    if (this.notifAudioCtx) {
      this.notifAudioCtx.close();
    }
    if (this.swMessageHandler) {
      navigator.serviceWorker.removeEventListener('message', this.swMessageHandler);
    }
  },
  methods: {
    updateClock() {
      const now = new Date();
      let h = now.getHours();
      const m = now.getMinutes();
      const s = now.getSeconds();
      this.period = h >= 12 ? "pm" : "am";
      h = h % 12 || 12;
      this.hours = h.toString().padStart(2, "0").replace(/^0/, "");
      this.minutes = m.toString().padStart(2, "0");
      this.seconds = s.toString().padStart(2, "0");

      // Format hours for display (no leading zero for time display)
      const hourNum = h;
      this.hours = hourNum < 10 ? `${hourNum}` : `${hourNum}`;
      this.minutes = m.toString().padStart(2, "0");

      this.currentDateThai = moment()
        .locale("en")
        .format("dddd, MMMM D");

      if (this.clockInMoment) {
        const diff = moment.duration(moment().diff(this.clockInMoment));
        const h = Math.floor(diff.asHours());
        const m = diff.minutes();
        const s = diff.seconds();
        this.workDuration = `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
      }
    },
    async loadAvatar() {
      try {
        const res = await this.$http.get("/portal/profile/get_img/", {
          responseType: "blob",
        });
        if (res.status === 200) {
          this.avatarUrl = URL.createObjectURL(new Blob([res.data]));
        }
      } catch {
        this.avatarUrl = "";
      }
    },
    async handleClockIn() {
      this.clockInLoading = true;
      // Simulated delay for premium feel
      setTimeout(() => {
        const now = moment();
        this.clockedTime = now.format("h:mm A");
        this.clockInTimeDisplayed = now.format("HH:mm");
        this.clockInMoment = now;
        this.resultState = "success";
        this.resultTitle = "Clocked In!";
        this.resultMessage = `You have successfully clocked in at`;
        this.clockInLoading = false;
      }, 800);
    },
    async handleClockOut() {
      this.clockOutLoading = true;
      // Simulated delay for premium feel
      setTimeout(() => {
        const now = moment();
        this.clockedTime = now.format("h:mm A");
        this.resultState = "success";
        this.resultTitle = "Clocked Out!";
        this.resultMessage = `You have successfully clocked out at`;
        // Clear work timer
        this.clockInMoment = null;
        this.clockInTimeDisplayed = "";
        this.workDuration = "00:00:00";
        this.clockOutLoading = false;
      }, 800);
    },
    async checkCurrentStatus() {
      try {
        const res = await this.$http.get("/hri/time_attendance/?limit=10");
        if (res.status === 200 && res.data.results.length > 0) {
          const todayStr = moment().format("YYYY-MM-DD");
          const todayRecords = res.data.results
            .filter(r => moment(r.created_at).format("YYYY-MM-DD") === todayStr)
            .sort((a, b) => moment(a.created_at) - moment(b.created_at));

          if (todayRecords.length > 0) {
            // Check if it's an odd number of records (meaning clocked in but not out yet)
            // Or if the last record is in the morning and there's only 1.
            // Following timeattandance.vue logic: records < 12 are IN.
            const firstRecord = todayRecords[0];
            const firstTime = moment(firstRecord.created_at);
            
            // If the first record is before 12 and it's currently still "work day" (simplification)
            // Or if we have an odd number of records.
            if (todayRecords.length % 2 !== 0) {
              this.clockInMoment = firstTime;
              this.clockInTimeDisplayed = firstTime.format("HH:mm");
            }
          }
        }
      } catch (error) {
        console.error("Failed to check status", error);
      }
    },
    handleCancel() {
      this.$router.push("/hri");
    },
    resetState() {
      this.resultState = null;
      this.resultTitle = "";
      this.resultMessage = "";
      this.clockedTime = "";
    },
    ringDoorbell() {
      if (this.isRinging) return;
      this.isRinging = true;

      // Use the unified AudioContext
      const ctx = this.ensureAudioContext();
      const masterGain = ctx.createGain();
      masterGain.gain.value = 0.9;
      masterGain.connect(ctx.destination);

      // Helper: play a single chime tone with harmonics
      const playChime = (freq, startTime, duration) => {
        // Fundamental
        const osc1 = ctx.createOscillator();
        const gain1 = ctx.createGain();
        osc1.type = 'sine';
        osc1.frequency.value = freq;
        gain1.gain.setValueAtTime(0.7, startTime);
        gain1.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
        osc1.connect(gain1);
        gain1.connect(masterGain);
        osc1.start(startTime);
        osc1.stop(startTime + duration);

        // 2nd harmonic (octave)
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.type = 'sine';
        osc2.frequency.value = freq * 2;
        gain2.gain.setValueAtTime(0.3, startTime);
        gain2.gain.exponentialRampToValueAtTime(0.001, startTime + duration * 0.7);
        osc2.connect(gain2);
        gain2.connect(masterGain);
        osc2.start(startTime);
        osc2.stop(startTime + duration);

        // 3rd harmonic for richness
        const osc3 = ctx.createOscillator();
        const gain3 = ctx.createGain();
        osc3.type = 'sine';
        osc3.frequency.value = freq * 3;
        gain3.gain.setValueAtTime(0.12, startTime);
        gain3.gain.exponentialRampToValueAtTime(0.001, startTime + duration * 0.5);
        osc3.connect(gain3);
        gain3.connect(masterGain);
        osc3.start(startTime);
        osc3.stop(startTime + duration);

        // Strike impact (white noise burst)
        const bufferSize = ctx.sampleRate * 0.05;
        const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
        const data = noiseBuffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) {
          data[i] = (Math.random() * 2 - 1) * 0.3;
        }
        const noise = ctx.createBufferSource();
        noise.buffer = noiseBuffer;
        const noiseGain = ctx.createGain();
        noiseGain.gain.setValueAtTime(0.4, startTime);
        noiseGain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.08);
        noise.connect(noiseGain);
        noiseGain.connect(masterGain);
        noise.start(startTime);
        noise.stop(startTime + 0.1);
      };

      const now = ctx.currentTime;

      // Ding-Dong pattern: play twice for emphasis (like a front door bell)
      // DING (high note) - DONG (lower note)
      playChime(830, now, 1.2);           // Ding 1
      playChime(622, now + 0.5, 1.5);     // Dong 1
      playChime(830, now + 2.0, 1.2);     // Ding 2
      playChime(622, now + 2.5, 1.5);     // Dong 2

      // Reset button after sound finishes
      setTimeout(() => {
        this.isRinging = false;
        // Don't close the shared context
      }, 4200);
    },
    // ── Firebase Cloud Messaging ──
    async initFCM() {
      try {
        // Check if service workers are supported
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
          // eslint-disable-next-line no-console
          console.warn('Push messaging is not supported');
          return;
        }

        // Request notification permission
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          // eslint-disable-next-line no-console
          console.warn('Notification permission denied');
          return;
        }

        // Register service worker and wait until it's fully active
        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        
        // Wait for service worker to be active
        await navigator.serviceWorker.ready;
        
        // If the SW is installing or waiting, wait for it to activate
        if (registration.installing || registration.waiting) {
          const sw = registration.installing || registration.waiting;
          await new Promise((resolve) => {
            sw.addEventListener('statechange', (e) => {
              if (e.target.state === 'activated') {
                resolve();
              }
            });
            // In case it's already activated
            if (sw.state === 'activated') {
              resolve();
            }
          });
        }

        // eslint-disable-next-line no-console
        console.log('Service Worker active, requesting FCM token...');

        // Get FCM token
        const token = await getToken(messaging, {
          vapidKey: 'BBie3nwetPP_wTFSXO9ui2ZvEVogjSi0z0jCwQEIan_RrdFrR5MCSiMqoCxqTUCcgMEh3XG6DaxrHg3EO5PjlIM',
          serviceWorkerRegistration: registration,
        });

        if (token) {
          this.fcmToken = token;
          // eslint-disable-next-line no-console
          console.log('FCM Token:', token);
        } else {
          // eslint-disable-next-line no-console
          console.warn('No FCM token received');
        }

        // Listen for foreground messages
        onMessage(messaging, (payload) => {
          // eslint-disable-next-line no-console
          console.log('FCM Message received:', payload);
          this.handleIncomingNotification(payload);
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('FCM init error:', error);
      }
    },
    handleIncomingNotification(payload) {
      const title = payload.notification?.title || payload.data?.title || 'New Notification';
      const body = payload.notification?.body || payload.data?.body || '';

      // Save to notification history
      this.notifications.unshift({
        title,
        body,
        time: moment().format('HH:mm:ss'),
      });

      // Show toast
      this.notificationData = { title, body };
      this.showNotification = true;

      // Play notification chime and doorbell sound immediately
      const ctx = this.ensureAudioContext();
      if (ctx.state === 'suspended') {
        ctx.resume().then(() => {
          this.playNotificationSound();
          this.ringDoorbell();
        }).catch(err => console.warn('Audio auto-play blocked by browser:', err));
      } else {
        this.playNotificationSound();
        this.ringDoorbell();
      }

      // Auto-dismiss after 8 seconds
      if (this.notificationTimeout) {
        clearTimeout(this.notificationTimeout);
      }
      this.notificationTimeout = setTimeout(() => {
        this.showNotification = false;
      }, 8000);
    },
    ensureAudioContext() {
      if (!this.notifAudioCtx) {
        const AC = window.AudioContext || window.webkitAudioContext;
        this.notifAudioCtx = new AC();
      }
      return this.notifAudioCtx;
    },
    playNotificationSound() {
      const ctx = this.ensureAudioContext();
      const masterGain = ctx.createGain();
      masterGain.gain.value = 1.0;
      masterGain.connect(ctx.destination);

      const playTone = (freq, start, dur, vol) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(vol, start);
        gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
        osc.connect(gain);
        gain.connect(masterGain);
        osc.start(start);
        osc.stop(start + dur);

        // Harmonic for richness
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.type = 'sine';
        osc2.frequency.value = freq * 2;
        gain2.gain.setValueAtTime(vol * 0.3, start);
        gain2.gain.exponentialRampToValueAtTime(0.001, start + dur * 0.6);
        osc2.connect(gain2);
        gain2.connect(masterGain);
        osc2.start(start);
        osc2.stop(start + dur);
      };

      const now = ctx.currentTime;
      // Loud 3-note chime: C-E-G, repeated twice
      playTone(523, now, 0.5, 0.8);           // C5
      playTone(659, now + 0.18, 0.5, 0.8);    // E5
      playTone(784, now + 0.36, 1.0, 0.9);    // G5
      playTone(523, now + 1.2, 0.5, 0.7);     // C5 repeat
      playTone(659, now + 1.38, 0.5, 0.7);    // E5 repeat
      playTone(784, now + 1.56, 1.0, 0.8);    // G5 repeat

      // Disconnect masterGain after sound finishes to avoid memory leak
      setTimeout(() => masterGain.disconnect(), 4000);
    },
    dismissNotification() {
      this.showNotification = false;
      if (this.notificationTimeout) {
        clearTimeout(this.notificationTimeout);
      }
    },
    clearAllNotifications() {
      this.notifications = [];
      this.showNotifPanel = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

$purple-900: #1a0533;
$purple-800: #27005D;
$purple-700: #3b0a6e;
$purple-600: #4c1085;
$purple-500: #6b21a8;
$purple-400: #8b5cf6;
$purple-300: #a78bfa;
$purple-200: #c4b5fd;
$purple-100: #ede9fe;
$purple-50: #f5f3ff;

.timeclick-page {
  display: flex;
  height: 95vh;
  margin-top: 15px;
  max-height: 100vh;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  overflow: hidden;
}

/* ── Left Panel ── */
.timeclick-left {
  flex: 0 0 clamp(280px, 30vw, 380px);
  background: linear-gradient(165deg, $purple-700 0%, $purple-900 50%, darken($purple-900, 5%) 100%);
  color: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: clamp(30px, 5vh, 60px) clamp(24px, 3vw, 48px);
  position: relative;
  overflow: hidden;
  border-radius: 20px;

  &::before {
    content: '';
    position: absolute;
    top: -100px;
    right: -100px;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: rgba($purple-400, 0.08);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -60px;
    left: -60px;
    width: 200px;
    height: 200px;
    border-radius: 50%;
    background: rgba($purple-300, 0.06);
  }
}

.left-content {
  position: relative;
  z-index: 1;
}

.clock-date {
  font-size: clamp(13px, 1.5vw, 16px);
  font-weight: 500;
  color: $purple-200;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.clock-time {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.clock-hours {
  font-size: clamp(48px, 8vw, 96px);
  font-weight: 800;
  line-height: 1;
  letter-spacing: -3px;
  background: linear-gradient(180deg, #ffffff 0%, $purple-200 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.clock-period {
  font-size: clamp(18px, 2.5vw, 32px);
  font-weight: 600;
  color: $purple-300;
  align-self: flex-end;
  margin-bottom: 4px;
}

.clock-in-info {
  margin-top: 24px;
  font-size: clamp(14px, 1.8vw, 18px);
  font-weight: 600;
  color: #ffffff;
  background: rgba(255, 255, 255, 0.12);
  padding: 10px 20px;
  border-radius: 50px;
  backdrop-filter: blur(10px);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);

  &::before {
    content: '●';
    color: #4ade80;
    font-size: 12px;
    animation: pulse-dot 2s infinite;
  }
}

@keyframes pulse-dot {
  0% { opacity: 1; }
  50% { opacity: 0.4; }
  100% { opacity: 1; }
}

.work-timer {
  margin-top: 16px;
  font-size: clamp(16px, 2.2vw, 20px);
  font-weight: 700;
  color: $purple-700;
  background: $purple-50;
  padding: 12px 24px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 4px 12px rgba($purple-600, 0.12);
  border-left: 4px solid $purple-600;
  animation: timer-glow 3s infinite ease-in-out;
}

@keyframes timer-glow {
  0%, 100% { box-shadow: 0 4px 12px rgba($purple-600, 0.12); }
  50% { box-shadow: 0 4px 20px rgba($purple-600, 0.25); }
}

.left-branding {
  position: relative;
  z-index: 1;
}

.branding-org {
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 2px;
}

.branding-location {
  font-size: 12px;
  color: $purple-300;
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.branding-logo {
  height: 40px;
  opacity: 0.85;
  filter: brightness(1.2);
}

/* ── Doorbell Button ── */
.doorbell-btn {
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px 20px;
  border: 2px solid rgba($purple-300, 0.4);
  border-radius: 12px;
  background: rgba($purple-400, 0.15);
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
  backdrop-filter: blur(4px);

  &:hover:not(:disabled) {
    background: rgba($purple-400, 0.3);
    border-color: rgba($purple-200, 0.6);
    transform: scale(1.03);
    box-shadow: 0 0 20px rgba($purple-400, 0.3);
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.85;
  }

  &.doorbell-ringing {
    animation: pulse-ring 0.6s ease-in-out infinite;
    background: rgba(#fbbf24, 0.25);
    border-color: rgba(#fbbf24, 0.6);
    box-shadow: 0 0 24px rgba(#fbbf24, 0.25);

    .doorbell-icon {
      animation: shake-bell 0.4s ease-in-out infinite;
    }
  }

  &.doorbell-locked {
    border-color: rgba($purple-300, 0.6);
    background: rgba($purple-400, 0.08);
    animation: pulse-unlock 2s ease-in-out infinite;
    
    &:hover {
      background: rgba($purple-400, 0.2);
    }
  }
}

@keyframes pulse-unlock {
  0%, 100% { border-color: rgba($purple-300, 0.4); box-shadow: 0 0 0 rgba($purple-400, 0); }
  50% { border-color: rgba($purple-300, 0.8); box-shadow: 0 0 12px rgba($purple-400, 0.2); }
}

.doorbell-icon {
  font-size: 22px;
  line-height: 1;
}

.doorbell-label {
  letter-spacing: 0.3px;
}

@keyframes shake-bell {
  0%, 100% { transform: rotate(0deg); }
  20% { transform: rotate(18deg); }
  40% { transform: rotate(-18deg); }
  60% { transform: rotate(12deg); }
  80% { transform: rotate(-12deg); }
}

@keyframes pulse-ring {
  0%, 100% { box-shadow: 0 0 12px rgba(#fbbf24, 0.2); }
  50% { box-shadow: 0 0 28px rgba(#fbbf24, 0.45); }
}

/* ── Right Panel ── */
.timeclick-right {
  flex: 1;
  background: #fafafa;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.right-content {
  max-width: 480px;
  width: 100%;
  padding: clamp(16px, 3vh, 40px) clamp(16px, 3vw, 40px);
  text-align: center;
}

.greeting-section {
  margin-bottom: clamp(16px, 3vh, 36px);
}

.greeting-text {
  font-size: clamp(20px, 2.5vw, 28px);
  font-weight: 800;
  color: $purple-900;
  margin-bottom: 4px;
  line-height: 1.3;
}

.greeting-subtitle {
  font-size: clamp(13px, 1.2vw, 15px);
  color: #8c8c8c;
  font-weight: 400;
  margin: 0;
}

/* ── Avatar ── */
.avatar-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  margin-bottom: clamp(16px, 3vh, 40px);
}

.avatar-line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, $purple-200, transparent);
}

.avatar-wrapper {
  padding: 0 20px;
}

.user-avatar {
  border: 3px solid $purple-100;
  box-shadow: 0 6px 24px rgba($purple-500, 0.15);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
}

/* ── Action Buttons ── */
.actions-section {
  display: flex;
  flex-direction: column;
  gap: clamp(8px, 1.5vh, 14px);
  margin-bottom: clamp(12px, 2vh, 32px);
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
}

.btn-clock-in {
  height: clamp(42px, 5vh, 52px) !important;
  border-radius: 10px !important;
  font-size: clamp(14px, 1.2vw, 16px) !important;
  font-weight: 700 !important;
  background: linear-gradient(135deg, $purple-600, $purple-500) !important;
  border: none !important;
  color: #ffffff !important;
  box-shadow: 0 4px 16px rgba($purple-500, 0.35) !important;
  transition: all 0.25s ease !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 8px !important;

  &:hover {
    background: linear-gradient(135deg, $purple-500, $purple-400) !important;
    transform: translateY(-2px) !important;
    box-shadow: 0 6px 24px rgba($purple-500, 0.45) !important;
  }

  &:active {
    transform: translateY(0) !important;
  }
}

.btn-request {
  height: clamp(42px, 5vh, 52px) !important;
  border-radius: 10px !important;
  font-size: clamp(14px, 1.2vw, 16px) !important;
  font-weight: 600 !important;
  background: #ffffff !important;
  border: 2px solid $purple-400 !important;
  color: $purple-600 !important;
  transition: all 0.25s ease !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 8px !important;

  &:hover {
    background: $purple-50 !important;
    border-color: $purple-500 !important;
    transform: translateY(-2px) !important;
    box-shadow: 0 4px 12px rgba($purple-500, 0.15) !important;
  }
}

/* ── Cancel Link ── */
.cancel-section {
  text-align: center;
}

.cancel-link {
  color: $purple-400;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: $purple-600;
  }
}

/* ── Result Overlay ── */
.result-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.result-card {
  text-align: center;
  padding: clamp(24px, 4vh, 48px);
}

.result-icon {
  font-size: clamp(48px, 6vh, 72px);
  margin-bottom: 16px;
}

.result-icon-success {
  color: #52c41a;
}

.result-icon-error {
  color: #ff4d4f;
}

.result-title {
  font-size: clamp(22px, 3vw, 28px);
  font-weight: 800;
  color: $purple-900;
  margin-bottom: 6px;
}

.result-message {
  font-size: 14px;
  color: #8c8c8c;
  margin-bottom: 4px;
}

.result-time {
  font-size: clamp(24px, 3vw, 32px);
  font-weight: 700;
  color: $purple-600;
  margin-bottom: 24px;
}

.btn-done {
  height: 44px !important;
  min-width: 140px !important;
  border-radius: 10px !important;
  font-size: 15px !important;
  font-weight: 700 !important;
  background: linear-gradient(135deg, $purple-600, $purple-500) !important;
  border: none !important;
  color: #ffffff !important;
  box-shadow: 0 4px 16px rgba($purple-500, 0.35) !important;
}

/* ── Transition ── */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* ── Notification Slide ── */
.slide-notification-enter-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.slide-notification-leave-active {
  transition: all 0.3s ease-in;
}
.slide-notification-enter,
.slide-notification-leave-to {
  transform: translateX(120%);
  opacity: 0;
}

.slide-panel-enter-active {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.slide-panel-leave-active {
  transition: all 0.2s ease-in;
}
.slide-panel-enter,
.slide-panel-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

/* ── FCM Notification Toast ── */
.fcm-notification-toast {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 14px;
  background: #ffffff;
  border-radius: 14px;
  padding: 16px 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba($purple-400, 0.15);
  max-width: 380px;
  min-width: 280px;
  cursor: pointer;
  animation: notif-glow 2s ease-in-out infinite;
}

@keyframes notif-glow {
  0%, 100% { box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba($purple-400, 0.15); }
  50% { box-shadow: 0 8px 32px rgba($purple-500, 0.25), 0 0 0 2px rgba($purple-400, 0.3); }
}

.notif-icon-wrapper {
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, $purple-100, $purple-50);
  display: flex;
  align-items: center;
  justify-content: center;
}

.notif-bell {
  font-size: 22px;
  animation: shake-bell 0.5s ease-in-out 3;
}

.notif-body {
  flex: 1;
  min-width: 0;
}

.notif-title {
  font-size: 14px;
  font-weight: 700;
  color: $purple-900;
  margin: 0 0 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notif-message {
  font-size: 13px;
  color: #8c8c8c;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notif-close {
  flex: 0 0 auto;
  background: none;
  border: none;
  color: #bfbfbf;
  font-size: 14px;
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;

  &:hover {
    color: $purple-600;
  }
}

/* ── Notification Badge ── */
.notif-badge {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 50;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, $purple-600, $purple-500);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba($purple-500, 0.35);
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
}

.notif-count {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #ff4d4f;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  border: 2px solid #fafafa;
}

/* ── Notification Panel ── */
.notif-panel {
  position: absolute;
  top: 72px;
  right: 20px;
  z-index: 60;
  width: 340px;
  max-height: 360px;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.notif-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid #f0f0f0;

  h4 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: $purple-900;
  }
}

.notif-panel-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.notif-panel-item {
  display: flex;
  gap: 12px;
  padding: 10px 18px;
  transition: background 0.15s;

  &:hover {
    background: $purple-50;
  }
}

.notif-panel-dot {
  flex: 0 0 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: $purple-400;
  margin-top: 6px;
}

.notif-panel-content {
  flex: 1;
  min-width: 0;
}

.notif-panel-title {
  font-size: 13px;
  font-weight: 600;
  color: $purple-900;
  margin: 0 0 2px;
}

.notif-panel-body {
  font-size: 12px;
  color: #8c8c8c;
  margin: 0 0 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notif-panel-time {
  font-size: 11px;
  color: #bfbfbf;
}

.notif-panel-empty {
  text-align: center;
  padding: 32px;
  color: #bfbfbf;
  font-size: 13px;
}

/* ── Tablet & Small Screens (vertical layout) ── */
@media (max-width: 768px) {
  .timeclick-page {
    flex-direction: column;
  }

  .timeclick-left {
    flex: 0 0 auto;
    padding: 20px 24px;
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }

  .clock-date {
    font-size: 13px;
    margin-bottom: 4px;
  }

  .clock-hours {
    font-size: 48px;
    letter-spacing: -2px;
  }

  .clock-period {
    font-size: 18px;
    margin-bottom: 2px;
  }

  .left-branding {
    text-align: right;
  }

  .branding-org {
    font-size: 14px;
  }

  .branding-location {
    justify-content: flex-end;
    font-size: 11px;
    margin-bottom: 8px;
  }

  .branding-logo {
    height: 30px;
  }

  .doorbell-btn {
    margin-top: 8px;
    padding: 8px 14px;
    font-size: 13px;
    border-radius: 8px;

    .doorbell-icon {
      font-size: 18px;
    }
  }

  .timeclick-right {
    flex: 1;
  }

  .right-content {
    padding: 20px 24px;
  }

  .greeting-section {
    margin-bottom: 16px;
  }

  .greeting-text {
    font-size: 20px;
  }

  .greeting-subtitle {
    font-size: 13px;
  }

  .avatar-section {
    margin-bottom: 16px;
  }

  .user-avatar {
    width: 80px !important;
    height: 80px !important;
  }

  .avatar-wrapper {
    padding: 0 16px;
  }

  .actions-section {
    gap: 10px;
    margin-bottom: 16px;
    max-width: 280px;
  }

  .btn-clock-in,
  .btn-request {
    height: 44px !important;
    font-size: 14px !important;
  }

  .cancel-link {
    font-size: 13px;
  }
}

/* ── Very Small Phones ── */
@media (max-width: 480px) {
  .timeclick-left {
    padding: 16px 20px;
  }

  .clock-hours {
    font-size: 40px;
  }

  .clock-period {
    font-size: 16px;
  }

  .doorbell-btn {
    margin-top: 6px;
    padding: 6px 12px;
    font-size: 12px;
    border-radius: 6px;
    gap: 6px;

    .doorbell-icon {
      font-size: 16px;
    }
  }

  .right-content {
    padding: 16px 20px;
  }

  .greeting-text {
    font-size: 18px;
  }

  .user-avatar {
    width: 64px !important;
    height: 64px !important;
    border-width: 2px !important;
  }

  .avatar-section {
    margin-bottom: 12px;
  }

  .actions-section {
    gap: 8px;
    margin-bottom: 12px;
    max-width: 100%;
  }

  .btn-clock-in,
  .btn-request {
    height: 40px !important;
    font-size: 13px !important;
    border-radius: 8px !important;
  }
}

/* ── Short Screens / Landscape Mobile ── */
@media (max-height: 600px) and (max-width: 768px) {
  .timeclick-left {
    padding: 12px 20px;
  }

  .clock-hours {
    font-size: 36px;
  }

  .clock-period {
    font-size: 14px;
  }

  .branding-logo {
    height: 24px;
  }

  .greeting-section {
    margin-bottom: 8px;
  }

  .greeting-text {
    font-size: 16px;
  }

  .greeting-subtitle {
    font-size: 12px;
  }

  .avatar-section {
    margin-bottom: 8px;
  }

  .user-avatar {
    width: 48px !important;
    height: 48px !important;
  }

  .actions-section {
    gap: 6px;
    margin-bottom: 8px;
  }

  .btn-clock-in,
  .btn-request {
    height: 36px !important;
    font-size: 12px !important;
  }
}

/* ── Landscape Desktop Short ── */
@media (max-height: 600px) and (min-width: 769px) {
  .clock-hours {
    font-size: 56px;
  }

  .greeting-section {
    margin-bottom: 12px;
  }

  .avatar-section {
    margin-bottom: 16px;
  }

  .user-avatar {
    width: 72px !important;
    height: 72px !important;
  }

  .actions-section {
    gap: 8px;
    margin-bottom: 16px;
  }

  .btn-clock-in,
  .btn-request {
    height: 40px !important;
  }
}
</style>
